import type { Locale } from '@xdocs/i18n';

/**
 * STOP-GAP local catalog for the `reader:` scope (F66), following the exact
 * `documents:` precedent (`apps/web/src/routes/documents/documents.i18n.ts`)
 * — NOT a second i18n runtime (E18/§13.3 ban), just physically scoped here
 * because `packages/i18n/**` is outside this task's ownership boundary.
 *
 * OPEN ISSUE: fold into `packages/i18n/src/catalogs/{en,ro}/reader.json`.
 */
const en = {
  'close': 'Close',
  'progress': 'Chapter {current} of {total}',
  'chapters.title': 'Chapters',
  'chapters.close': 'Close chapter list',
  'nav.previous': 'Previous',
  'nav.next': 'Next chapter',
  'theme.light': 'Light',
  'theme.sepia': 'Sepia',
  'theme.dark': 'Dark',
  'theme.highContrast': 'High contrast',
  'theme.label': 'Reading theme',
  'fontSize.label': 'Text size',
  'fontSize.decrease': 'Decrease text size',
  'fontSize.increase': 'Increase text size',
  'layout.label': 'Layout',
  'layout.scroll': 'Scroll',
  'layout.paginated': 'Paginated',
  'loading.title': 'Opening the reader…',
  'error.title': "Couldn't load this document.",
  'error.retry': 'Retry',
  'notFound.title': "We couldn't find this document.",
  'notFound.cta': 'Back',
  'forbidden.title': "You don't have access to this document.",
  'forbidden.description': 'This link may have been revoked or expired, or the owner changed who can view it.',
  'forbidden.cta': 'Back to documents',
  'empty.title': "This document doesn't have any content yet.",
  'empty.description': 'Come back once generation has finished.',
  'newVersion.title': 'A new version is available',
  'newVersion.description': 'This document was updated while you were reading. Switch to keep reading the latest version.',
  'newVersion.cta': 'Switch to new version',
  'newVersion.dismiss': 'Keep reading this version',
  'versionSwitcher.label': 'Version',
  'versionSwitcher.language': 'Language',
} as const;

const ro: Record<keyof typeof en, string> = {
  'close': 'Închide',
  'progress': 'Capitolul {current} din {total}',
  'chapters.title': 'Capitole',
  'chapters.close': 'Închide lista de capitole',
  'nav.previous': 'Anterior',
  'nav.next': 'Capitolul următor',
  'theme.light': 'Luminos',
  'theme.sepia': 'Sepia',
  'theme.dark': 'Întunecat',
  'theme.highContrast': 'Contrast ridicat',
  'theme.label': 'Temă de citire',
  'fontSize.label': 'Dimensiune text',
  'fontSize.decrease': 'Micșorează textul',
  'fontSize.increase': 'Mărește textul',
  'layout.label': 'Aspect',
  'layout.scroll': 'Derulare',
  'layout.paginated': 'Pagini',
  'loading.title': 'Se deschide cititorul…',
  'error.title': 'Nu am putut încărca acest document.',
  'error.retry': 'Reîncearcă',
  'notFound.title': 'Nu am găsit acest document.',
  'notFound.cta': 'Înapoi',
  'forbidden.title': 'Nu ai acces la acest document.',
  'forbidden.description': 'Acest link poate fi revocat sau expirat, sau proprietarul a schimbat cine poate vedea documentul.',
  'forbidden.cta': 'Înapoi la documente',
  'empty.title': 'Acest document nu are încă niciun conținut.',
  'empty.description': 'Revino după ce generarea s-a încheiat.',
  'newVersion.title': 'O nouă versiune este disponibilă',
  'newVersion.description': 'Acest document a fost actualizat în timp ce citeai. Comută pentru a continua cu cea mai recentă versiune.',
  'newVersion.cta': 'Comută la noua versiune',
  'newVersion.dismiss': 'Continuă cu această versiune',
  'versionSwitcher.label': 'Versiune',
  'versionSwitcher.language': 'Limbă',
};

export const READER_CATALOG: Record<Locale, Record<keyof typeof en, string>> = { en, ro };

export type ReaderCatalogKey = keyof typeof en;
